import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Modal } from "react-bootstrap";
import { connect } from "react-redux";
import {
  HiOutlineArrowRight,
  HiOutlineCash,
  HiOutlineClock,
  HiOutlineEye,
  HiOutlineEyeOff,
  HiOutlineGift,
  HiOutlineLightningBolt,
  HiOutlineLockClosed,
  HiOutlineMail,
  HiOutlineShieldCheck,
  HiOutlineUser,
  HiOutlineUserAdd,
} from "react-icons/hi";
import { FiX } from "react-icons/fi";
import Labels from "../../services/apiLabels";
import { POST } from "../../constant/apiMethods";
import {
  LOGIN,
  SIGN_UP,
  TOASTER_SHOW,
  toaster_e,
} from "../../constant";
import {
  clearPendingReferralCode,
  getPendingReferralCode,
  readSignupEmailFromSearch,
} from "../../utils/userIdentity";
import { markOnboardingTourPending } from "../../utils/onboardingTour";
import { USER_TYPE_CLIENT } from "../../utils/userTypes";
import GoogleAuthButton from "../GoogleAuthButton";
import "../../pages/Login Page/client-login.css";
import "./ValuationAuthModal.css";

const publicUrl = process.env.PUBLIC_URL || "";
const VISUAL_IMAGE = `${publicUrl}/images/hero-evaluation.jpg`;

const EMPTY_LOGIN = { username: "", password: "" };
const EMPTY_SIGNUP = {
  first_name: "",
  last_name: "",
  email: "",
  username: "",
  password: "",
  confirm_password: "",
  ref_code: "",
};

function FieldError({ id, message }) {
  return (
    <p
      id={id}
      className={`pi-auth-modal__field-error${message ? " is-visible" : ""}`}
      role={message ? "alert" : undefined}
      aria-hidden={message ? undefined : true}
    >
      {message || ""}
    </p>
  );
}

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());
}

function ValuationAuthModal({
  open,
  onClose,
  onAuthSuccess,
  login,
  signUp,
  googleLogin,
  toasterShow,
  auth,
}) {
  const [mode, setMode] = useState("login");
  const [loginForm, setLoginForm] = useState(EMPTY_LOGIN);
  const [signupForm, setSignupForm] = useState(EMPTY_SIGNUP);
  const [loginErrors, setLoginErrors] = useState({});
  const [signupErrors, setSignupErrors] = useState({});
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showSignupPassword, setShowSignupPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const successFiredRef = useRef(false);
  const formSideRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setMode("login");
    setLoginForm(EMPTY_LOGIN);
    setSignupForm({
      ...EMPTY_SIGNUP,
      ref_code: getPendingReferralCode(),
      email: readSignupEmailFromSearch(),
    });
    setLoginErrors({});
    setSignupErrors({});
    setShowLoginPassword(false);
    setShowSignupPassword(false);
    setShowConfirmPassword(false);
    setSubmitting(false);
    successFiredRef.current = false;
  }, [open]);

  useEffect(() => {
    if (!open || !auth?.token || successFiredRef.current) return;
    successFiredRef.current = true;
    setSubmitting(false);
    clearPendingReferralCode();
    onAuthSuccess?.();
  }, [auth?.token, open, onAuthSuccess]);

  useEffect(() => {
    if (!submitting) return;
    if (auth?.loging || auth?.creating) return;
    if (!auth?.token) setSubmitting(false);
  }, [auth?.loging, auth?.creating, auth?.token, submitting]);

  const clearLoginError = (key) => {
    setLoginErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const clearSignupError = (key) => {
    setSignupErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const switchMode = (nextMode) => {
    setMode(nextMode);
    setLoginErrors({});
    setSignupErrors({});
    // Reset form column scroll when switching tabs
    window.requestAnimationFrame(() => {
      if (formSideRef.current) formSideRef.current.scrollTop = 0;
    });
  };

  const fireSuccess = () => {
    setSubmitting(false);
    if (successFiredRef.current) return;
    successFiredRef.current = true;
    onAuthSuccess?.();
  };

  const handleLogin = (event) => {
    event.preventDefault();
    const username = loginForm.username.trim();
    const password = loginForm.password;
    const nextErrors = {};
    if (!username) nextErrors.username = "Please enter your user name.";
    if (!password) nextErrors.password = "Please enter your password.";
    if (Object.keys(nextErrors).length) {
      setLoginErrors(nextErrors);
      window.setTimeout(() => {
        document
          .querySelector(".pi-auth-modal__panel .pi-login__field.is-invalid")
          ?.scrollIntoView({ block: "nearest", behavior: "smooth" });
      }, 50);
      return;
    }
    setLoginErrors({});
    setSubmitting(true);
    login({ username, password }, fireSuccess);
  };

  const handleSignup = (event) => {
    event.preventDefault();
    const first_name = signupForm.first_name.trim();
    const last_name = signupForm.last_name.trim();
    const email = signupForm.email.trim();
    const username = signupForm.username.trim();
    const password = signupForm.password;
    const confirm_password = signupForm.confirm_password;
    const ref_code = String(signupForm.ref_code || "").trim();
    const nextErrors = {};

    if (!first_name) nextErrors.first_name = "Please enter your first name.";
    if (!last_name) nextErrors.last_name = "Please enter your last name.";
    if (!email) nextErrors.email = "Please enter your email.";
    else if (!isValidEmail(email)) {
      nextErrors.email = "Please enter a valid email address.";
    }
    if (!username) nextErrors.username = "Please enter a user name.";
    if (!password) nextErrors.password = "Please enter a password.";
    if (!confirm_password) {
      nextErrors.confirm_password = "Please confirm your password.";
    } else if (password && password !== confirm_password) {
      nextErrors.confirm_password = "Passwords do not match.";
    }

    if (Object.keys(nextErrors).length) {
      setSignupErrors(nextErrors);
      window.setTimeout(() => {
        document
          .querySelector(".pi-auth-modal__panel .pi-login__field.is-invalid")
          ?.scrollIntoView({ block: "nearest", behavior: "smooth" });
      }, 50);
      return;
    }

    setSignupErrors({});
    setSubmitting(true);
    const payload = {
      first_name,
      last_name,
      username,
      email,
      password,
      type: USER_TYPE_CLIENT,
    };
    if (ref_code) {
      payload.ref_code = ref_code;
    }
    markOnboardingTourPending(USER_TYPE_CLIENT);
    signUp(payload);
  };

  const onGoogleCredential = (credential, error) => {
    if (error || !credential) {
      toasterShow({
        show: true,
        autoHide: true,
        type: toaster_e,
        heading: mode === "signup" ? "Google sign-up" : "Google sign-in",
        description:
          error?.message ||
          (mode === "signup"
            ? "Google sign-up was cancelled or failed."
            : "Google sign-in was cancelled or failed."),
      });
      return;
    }
    const payload = {
      id_token: credential,
      type: USER_TYPE_CLIENT,
    };
    if (mode === "signup") {
      const ref_code = String(signupForm.ref_code || "").trim();
      if (ref_code) payload.ref_code = ref_code;
      markOnboardingTourPending(USER_TYPE_CLIENT);
    }
    setSubmitting(true);
    googleLogin(payload, fireSuccess);
  };

  const busy = submitting || !!auth?.loging || !!auth?.creating;
  const refCode = String(signupForm.ref_code || "").trim();
  const isSignup = mode === "signup";

  const canSubmitLogin =
    Boolean(loginForm.username.trim()) && Boolean(loginForm.password);
  const canSubmitSignup = useMemo(() => {
    const first_name = signupForm.first_name.trim();
    const last_name = signupForm.last_name.trim();
    const email = signupForm.email.trim();
    const username = signupForm.username.trim();
    const password = signupForm.password;
    const confirm_password = signupForm.confirm_password;
    if (!first_name || !last_name || !email || !username || !password) {
      return false;
    }
    if (!isValidEmail(email)) return false;
    if (!confirm_password || password !== confirm_password) return false;
    return true;
  }, [signupForm]);

  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    const previousPaddingRight = document.body.style.paddingRight;
    const scrollbarGap =
      window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = "hidden";
    if (scrollbarGap > 0) {
      document.body.style.paddingRight = `${scrollbarGap}px`;
    }
    document.body.classList.add("pi-auth-modal-open");

    const isEditableTarget = (target) => {
      if (!target || !(target instanceof Element)) return false;
      const tag = target.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
      if (target.isContentEditable) return true;
      return Boolean(
        target.closest(
          "input, textarea, select, [contenteditable='true']"
        )
      );
    };

    const isInsideModalShell = (target) => {
      if (!(target instanceof Element)) return false;
      return Boolean(target.closest(".pi-auth-modal__shell"));
    };

    const scrollFormByKey = (key) => {
      const side = formSideRef.current;
      if (!side) return false;
      const max = Math.max(side.scrollHeight - side.clientHeight, 0);
      if (max <= 0) return false;

      const page = Math.max(Math.round(side.clientHeight * 0.85), 120);
      let delta = 0;
      if (key === "ArrowDown") delta = 72;
      else if (key === "ArrowUp") delta = -72;
      else if (key === "PageDown") delta = page;
      else if (key === "PageUp") delta = -page;
      else if (key === "Home") {
        side.scrollTop = 0;
        return true;
      } else if (key === "End") {
        side.scrollTop = max;
        return true;
      } else if (key === " " || key === "Spacebar") {
        delta = page;
      } else {
        return false;
      }

      const prev = side.scrollTop;
      const next = Math.max(0, Math.min(prev + delta, max));
      if (Math.abs(next - prev) < 1) return false;
      side.scrollTop = next;
      return true;
    };

    // Body scroll is locked — arrow/page keys must move the form column scrollbar
    const onKeyDown = (event) => {
      const key = event.key;
      const scrollKeys = [
        "ArrowUp",
        "ArrowDown",
        "PageUp",
        "PageDown",
        "Home",
        "End",
        " ",
        "Spacebar",
      ];
      if (!scrollKeys.includes(key)) return;

      const active =
        document.activeElement instanceof Element
          ? document.activeElement
          : null;
      const inModal =
        isInsideModalShell(event.target) ||
        isInsideModalShell(active) ||
        Boolean(document.querySelector(".pi-auth-modal.show, .pi-auth-modal.modal"));

      if (!inModal) {
        event.preventDefault();
        return;
      }

      // Space: keep typing / button activation
      if (key === " " || key === "Spacebar") {
        if (isEditableTarget(active) || isEditableTarget(event.target)) return;
        const el = active || (event.target instanceof Element ? event.target : null);
        const tag = el?.tagName;
        if (
          tag === "BUTTON" ||
          tag === "A" ||
          el?.getAttribute?.("role") === "button" ||
          el?.getAttribute?.("role") === "tab"
        ) {
          return;
        }
      }

      // Home/End in inputs: keep caret jump to start/end of field
      if (
        (key === "Home" || key === "End") &&
        (isEditableTarget(active) || isEditableTarget(event.target))
      ) {
        return;
      }

      // ArrowUp/Down + PageUp/Down always scroll the form (fields are single-line)
      if (scrollFormByKey(key)) {
        event.preventDefault();
        event.stopPropagation();
      } else if (
        key === "ArrowUp" ||
        key === "ArrowDown" ||
        key === "PageUp" ||
        key === "PageDown"
      ) {
        // At edge — still block page scroll behind the modal
        event.preventDefault();
      }
    };

    // Only block wheel on the backdrop — never touchmove (breaks mobile scroll)
    const blockWheelOnBackdrop = (event) => {
      if (isInsideModalShell(event.target)) return;
      event.preventDefault();
    };

    window.addEventListener("keydown", onKeyDown, { capture: true });
    document.addEventListener("wheel", blockWheelOnBackdrop, {
      passive: false,
      capture: true,
    });

    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.style.paddingRight = previousPaddingRight;
      document.body.classList.remove("pi-auth-modal-open");
      window.removeEventListener("keydown", onKeyDown, { capture: true });
      document.removeEventListener("wheel", blockWheelOnBackdrop, {
        capture: true,
      });
    };
  }, [open]);

  return (
    <Modal
      show={open}
      onHide={onClose}
      centered={false}
      backdrop="static"
      enforceFocus
      restoreFocus
      className="pi-auth-modal"
      dialogClassName={`pi-auth-modal__dialog${
        isSignup ? " pi-auth-modal__dialog--signup" : ""
      }`}
      aria-labelledby="valuation-auth-title"
    >
      <div className={`pi-auth-modal__shell${isSignup ? " is-signup" : ""}`}>
        <button
          type="button"
          className="pi-auth-modal__close"
          onClick={onClose}
          aria-label="Close"
        >
          <FiX size={17} strokeWidth={2.4} aria-hidden />
        </button>

        <aside
          className="pi-auth-modal__visual"
          style={{ backgroundImage: `url(${VISUAL_IMAGE})` }}
          aria-hidden="true"
        >
          <span className="pi-auth-modal__visual-shade" />
          <div className="pi-auth-modal__visual-copy">
            <p className="pi-auth-modal__visual-kicker">Free valuation</p>
            <h2>
              {isSignup ? (
                <>
                  Create an account.
                  <span> Unlock your valuation.</span>
                </>
              ) : (
                <>
                  Almost there.
                  <span> Sign in to continue.</span>
                </>
              )}
            </h2>
            <ul className="pi-auth-modal__points">
              <li>
                <HiOutlineLightningBolt size={16} />
                Instant vehicle valuation
              </li>
              <li>
                <HiOutlineClock size={16} />
                Book and track appointments
              </li>
              <li>
                <HiOutlineCash size={16} />
                Earn referral credits
              </li>
              <li>
                <HiOutlineShieldCheck size={16} />
                Secure account access
              </li>
            </ul>
          </div>
        </aside>

          <div
            className="pi-auth-modal__form-side"
            ref={formSideRef}
            tabIndex={-1}
          >
          <div
            className="pi-auth-modal__tabs"
            role="tablist"
            aria-label="Authentication"
          >
            <button
              type="button"
              role="tab"
              id="valuation-auth-tab-login"
              aria-selected={!isSignup}
              aria-controls="valuation-auth-panel"
              className={!isSignup ? "is-active" : undefined}
              onClick={() => switchMode("login")}
            >
              Log in
            </button>
            <button
              type="button"
              role="tab"
              id="valuation-auth-tab-signup"
              aria-selected={isSignup}
              aria-controls="valuation-auth-panel"
              className={isSignup ? "is-active" : undefined}
              onClick={() => switchMode("signup")}
            >
              Sign up
            </button>
          </div>

          <div
            id="valuation-auth-panel"
            role="tabpanel"
            aria-labelledby={
              isSignup
                ? "valuation-auth-tab-signup"
                : "valuation-auth-tab-login"
            }
            className="pi-auth-modal__panel"
          >
            <header className="pi-auth-modal__head">
              <p className="pi-label">AutoTrade Port</p>
              <h1 id="valuation-auth-title">
                {isSignup ? "Create your account" : "Welcome back"}
              </h1>
              <p className="pi-auth-modal__lead">
                {isSignup
                  ? "Sign up to get your free vehicle valuation."
                  : "Log in to continue your free vehicle valuation."}
              </p>
            </header>

            {isSignup && refCode ? (
              <div className="pi-login__ref-banner" role="status">
                <HiOutlineGift size={16} aria-hidden />
                Referral code applied: <strong>{refCode}</strong>
              </div>
            ) : null}

            {!isSignup ? (
              <>
                <form
                  className="pi-login__form pi-auth-modal__fields"
                  onSubmit={handleLogin}
                  autoComplete="on"
                  noValidate
                >
                  <label
                    className={`pi-login__field${
                      loginErrors.username ? " is-invalid" : ""
                    }`}
                  >
                    <span className="pi-login__field-label">
                      User name
                      <span className="pi-login__req" aria-hidden="true">
                        *
                      </span>
                    </span>
                    <span className="pi-login__control">
                      <HiOutlineUser size={18} aria-hidden />
                      <input
                        id="valuation-login-username"
                        type="text"
                        name="username"
                        autoComplete="username"
                        placeholder="Enter user name"
                        value={loginForm.username}
                        aria-invalid={Boolean(loginErrors.username)}
                        aria-describedby={
                          loginErrors.username
                            ? "valuation-login-username-error"
                            : undefined
                        }
                        onChange={(e) => {
                          clearLoginError("username");
                          setLoginForm((prev) => ({
                            ...prev,
                            username: e.target.value,
                          }));
                        }}
                        onBlur={() => {
                          if (!loginForm.username.trim()) {
                            setLoginErrors((prev) => ({
                              ...prev,
                              username: "Please enter your user name.",
                            }));
                          }
                        }}
                      />
                    </span>
                    <FieldError
                      id="valuation-login-username-error"
                      message={loginErrors.username}
                    />
                  </label>

                  <div
                    className={`pi-login__field${
                      loginErrors.password ? " is-invalid" : ""
                    }`}
                  >
                    <span
                      className="pi-login__field-label"
                      id="valuation-login-password-label"
                    >
                      Password
                      <span className="pi-login__req" aria-hidden="true">
                        *
                      </span>
                    </span>
                    <span className="pi-login__control">
                      <HiOutlineLockClosed size={18} aria-hidden />
                      <input
                        id="valuation-login-password"
                        type={showLoginPassword ? "text" : "password"}
                        name="password"
                        autoComplete="current-password"
                        placeholder="Enter password"
                        value={loginForm.password}
                        aria-invalid={Boolean(loginErrors.password)}
                        aria-describedby={
                          loginErrors.password
                            ? "valuation-login-password-error"
                            : undefined
                        }
                        onChange={(e) => {
                          clearLoginError("password");
                          setLoginForm((prev) => ({
                            ...prev,
                            password: e.target.value,
                          }));
                        }}
                        onBlur={() => {
                          if (!loginForm.password) {
                            setLoginErrors((prev) => ({
                              ...prev,
                              password: "Please enter your password.",
                            }));
                          }
                        }}
                        aria-labelledby="valuation-login-password-label"
                      />
                      <button
                        type="button"
                        className="pi-login__toggle-password"
                        onClick={() => setShowLoginPassword((prev) => !prev)}
                        aria-label={
                          showLoginPassword ? "Hide password" : "Show password"
                        }
                        aria-pressed={showLoginPassword}
                      >
                        {showLoginPassword ? (
                          <HiOutlineEyeOff size={18} aria-hidden />
                        ) : (
                          <HiOutlineEye size={18} aria-hidden />
                        )}
                      </button>
                    </span>
                    <FieldError
                      id="valuation-login-password-error"
                      message={loginErrors.password}
                    />
                  </div>

                  <div className="pi-login__meta">
                    <span />
                    <Link to="/forgetpassword" className="pi-login__link">
                      Forgot password?
                    </Link>
                  </div>

                  <button
                    type="submit"
                    className="pi-login__submit"
                    disabled={!canSubmitLogin || busy}
                  >
                    {busy && !auth?.creating ? "Signing in…" : "Sign in"}
                    {!(busy && !auth?.creating) ? (
                      <HiOutlineArrowRight size={18} aria-hidden />
                    ) : null}
                  </button>
                </form>

                <div className="pi-login__or" aria-hidden="true">
                  <span>or</span>
                </div>

                <GoogleAuthButton
                  text="signin_with"
                  disabled={busy}
                  onCredential={onGoogleCredential}
                />
              </>
            ) : (
              <>
                <form
                  className="pi-login__form pi-auth-modal__fields"
                  onSubmit={handleSignup}
                  autoComplete="on"
                  noValidate
                >
                  <div className="pi-login__row">
                    <label
                      className={`pi-login__field${
                        signupErrors.first_name ? " is-invalid" : ""
                      }`}
                    >
                      <span className="pi-login__field-label">
                        First name
                        <span className="pi-login__req" aria-hidden="true">
                          *
                        </span>
                      </span>
                      <span className="pi-login__control">
                        <HiOutlineUserAdd size={18} aria-hidden />
                        <input
                          id="valuation-signup-first"
                          type="text"
                          name="first_name"
                          autoComplete="given-name"
                          placeholder="First name"
                          value={signupForm.first_name}
                          aria-invalid={Boolean(signupErrors.first_name)}
                          aria-describedby={
                            signupErrors.first_name
                              ? "valuation-signup-first-error"
                              : undefined
                          }
                          onChange={(e) => {
                            clearSignupError("first_name");
                            setSignupForm((prev) => ({
                              ...prev,
                              first_name: e.target.value,
                            }));
                          }}
                          onBlur={() => {
                            if (!signupForm.first_name.trim()) {
                              setSignupErrors((prev) => ({
                                ...prev,
                                first_name: "Please enter your first name.",
                              }));
                            }
                          }}
                        />
                      </span>
                      <FieldError
                        id="valuation-signup-first-error"
                        message={signupErrors.first_name}
                      />
                    </label>

                    <label
                      className={`pi-login__field${
                        signupErrors.last_name ? " is-invalid" : ""
                      }`}
                    >
                      <span className="pi-login__field-label">
                        Last name
                        <span className="pi-login__req" aria-hidden="true">
                          *
                        </span>
                      </span>
                      <span className="pi-login__control">
                        <HiOutlineUserAdd size={18} aria-hidden />
                        <input
                          id="valuation-signup-last"
                          type="text"
                          name="last_name"
                          autoComplete="family-name"
                          placeholder="Last name"
                          value={signupForm.last_name}
                          aria-invalid={Boolean(signupErrors.last_name)}
                          aria-describedby={
                            signupErrors.last_name
                              ? "valuation-signup-last-error"
                              : undefined
                          }
                          onChange={(e) => {
                            clearSignupError("last_name");
                            setSignupForm((prev) => ({
                              ...prev,
                              last_name: e.target.value,
                            }));
                          }}
                          onBlur={() => {
                            if (!signupForm.last_name.trim()) {
                              setSignupErrors((prev) => ({
                                ...prev,
                                last_name: "Please enter your last name.",
                              }));
                            }
                          }}
                        />
                      </span>
                      <FieldError
                        id="valuation-signup-last-error"
                        message={signupErrors.last_name}
                      />
                    </label>
                  </div>

                    <label
                      className={`pi-login__field${
                        signupErrors.email ? " is-invalid" : ""
                      }`}
                    >
                      <span className="pi-login__field-label">
                        Email
                        <span className="pi-login__req" aria-hidden="true">
                          *
                        </span>
                      </span>
                    <span className="pi-login__control">
                      <HiOutlineMail size={18} aria-hidden />
                      <input
                        id="valuation-signup-email"
                        type="email"
                        name="email"
                        autoComplete="email"
                        placeholder="Enter email"
                        value={signupForm.email}
                        aria-invalid={Boolean(signupErrors.email)}
                        aria-describedby={
                          signupErrors.email
                            ? "valuation-signup-email-error"
                            : undefined
                        }
                        onChange={(e) => {
                          clearSignupError("email");
                          setSignupForm((prev) => ({
                            ...prev,
                            email: e.target.value,
                          }));
                        }}
                        onBlur={() => {
                          const value = signupForm.email.trim();
                          if (!value) {
                            setSignupErrors((prev) => ({
                              ...prev,
                              email: "Please enter your email.",
                            }));
                          } else if (!isValidEmail(value)) {
                            setSignupErrors((prev) => ({
                              ...prev,
                              email: "Please enter a valid email address.",
                            }));
                          }
                        }}
                      />
                    </span>
                    <FieldError
                      id="valuation-signup-email-error"
                      message={signupErrors.email}
                    />
                  </label>

                    <label
                      className={`pi-login__field${
                        signupErrors.username ? " is-invalid" : ""
                      }`}
                    >
                      <span className="pi-login__field-label">
                        User name
                        <span className="pi-login__req" aria-hidden="true">
                          *
                        </span>
                      </span>
                      <span className="pi-login__control">
                        <HiOutlineUser size={18} aria-hidden />
                        <input
                          id="valuation-signup-username"
                        type="text"
                        name="username"
                        autoComplete="username"
                        placeholder="Enter user name"
                        value={signupForm.username}
                        aria-invalid={Boolean(signupErrors.username)}
                        aria-describedby={
                          signupErrors.username
                            ? "valuation-signup-username-error"
                            : undefined
                        }
                        onChange={(e) => {
                          clearSignupError("username");
                          setSignupForm((prev) => ({
                            ...prev,
                            username: e.target.value,
                          }));
                        }}
                        onBlur={() => {
                          if (!signupForm.username.trim()) {
                            setSignupErrors((prev) => ({
                              ...prev,
                              username: "Please enter a user name.",
                            }));
                          }
                        }}
                      />
                    </span>
                    <FieldError
                      id="valuation-signup-username-error"
                      message={signupErrors.username}
                    />
                  </label>

                  <div className="pi-login__row pi-login__row--passwords">
                    <div
                      className={`pi-login__field${
                        signupErrors.password ? " is-invalid" : ""
                      }`}
                    >
                      <span
                        className="pi-login__field-label"
                        id="valuation-signup-password-label"
                      >
                        Password
                        <span className="pi-login__req" aria-hidden="true">
                          *
                        </span>
                      </span>
                      <span className="pi-login__control">
                        <HiOutlineLockClosed size={18} aria-hidden />
                        <input
                          id="valuation-signup-password"
                          type={showSignupPassword ? "text" : "password"}
                          name="password"
                          autoComplete="new-password"
                          placeholder="Enter password"
                          value={signupForm.password}
                          aria-invalid={Boolean(signupErrors.password)}
                          aria-describedby={
                            signupErrors.password
                              ? "valuation-signup-password-error"
                              : undefined
                          }
                          onChange={(e) => {
                            clearSignupError("password");
                            setSignupForm((prev) => ({
                              ...prev,
                              password: e.target.value,
                            }));
                          }}
                          onBlur={() => {
                            if (!signupForm.password) {
                              setSignupErrors((prev) => ({
                                ...prev,
                                password: "Please enter a password.",
                              }));
                            }
                          }}
                          aria-labelledby="valuation-signup-password-label"
                        />
                        <button
                          type="button"
                          className="pi-login__toggle-password"
                          onClick={() =>
                            setShowSignupPassword((prev) => !prev)
                          }
                          aria-label={
                            showSignupPassword
                              ? "Hide password"
                              : "Show password"
                          }
                          aria-pressed={showSignupPassword}
                        >
                          {showSignupPassword ? (
                            <HiOutlineEyeOff size={18} aria-hidden />
                          ) : (
                            <HiOutlineEye size={18} aria-hidden />
                          )}
                        </button>
                      </span>
                      <FieldError
                        id="valuation-signup-password-error"
                        message={signupErrors.password}
                      />
                    </div>

                    <div
                      className={`pi-login__field${
                        signupErrors.confirm_password ? " is-invalid" : ""
                      }`}
                    >
                      <span
                        className="pi-login__field-label"
                        id="valuation-signup-confirm-label"
                      >
                        Confirm password
                        <span className="pi-login__req" aria-hidden="true">
                          *
                        </span>
                      </span>
                      <span className="pi-login__control">
                        <HiOutlineLockClosed size={18} aria-hidden />
                        <input
                          id="valuation-signup-confirm"
                          type={showConfirmPassword ? "text" : "password"}
                          name="confirm_password"
                          autoComplete="new-password"
                          placeholder="Re-enter password"
                          value={signupForm.confirm_password}
                          aria-invalid={Boolean(signupErrors.confirm_password)}
                          aria-describedby={
                            signupErrors.confirm_password
                              ? "valuation-signup-confirm-error"
                              : undefined
                          }
                          onChange={(e) => {
                            clearSignupError("confirm_password");
                            setSignupForm((prev) => ({
                              ...prev,
                              confirm_password: e.target.value,
                            }));
                          }}
                          onBlur={() => {
                            const confirm = signupForm.confirm_password;
                            if (!confirm) {
                              setSignupErrors((prev) => ({
                                ...prev,
                                confirm_password:
                                  "Please confirm your password.",
                              }));
                            } else if (
                              signupForm.password &&
                              confirm !== signupForm.password
                            ) {
                              setSignupErrors((prev) => ({
                                ...prev,
                                confirm_password: "Passwords do not match.",
                              }));
                            }
                          }}
                          aria-labelledby="valuation-signup-confirm-label"
                        />
                        <button
                          type="button"
                          className="pi-login__toggle-password"
                          onClick={() =>
                            setShowConfirmPassword((prev) => !prev)
                          }
                          aria-label={
                            showConfirmPassword
                              ? "Hide confirm password"
                              : "Show confirm password"
                          }
                          aria-pressed={showConfirmPassword}
                        >
                          {showConfirmPassword ? (
                            <HiOutlineEyeOff size={18} aria-hidden />
                          ) : (
                            <HiOutlineEye size={18} aria-hidden />
                          )}
                        </button>
                      </span>
                      <FieldError
                        id="valuation-signup-confirm-error"
                        message={signupErrors.confirm_password}
                      />
                    </div>
                  </div>

                  <label className="pi-login__field">
                    <span className="pi-login__field-label">
                      Referral code <em>(optional)</em>
                    </span>
                    <span className="pi-login__control">
                      <HiOutlineGift size={18} aria-hidden />
                      <input
                        id="valuation-signup-ref"
                        type="text"
                        name="ref_code"
                        autoComplete="off"
                        placeholder="Enter referral code"
                        value={signupForm.ref_code}
                        onChange={(e) =>
                          setSignupForm((prev) => ({
                            ...prev,
                            ref_code: e.target.value,
                          }))
                        }
                      />
                    </span>
                  </label>

                  <button
                    type="submit"
                    className="pi-login__submit"
                    disabled={!canSubmitSignup || busy}
                  >
                    {auth?.creating
                      ? "Creating account…"
                      : "Create account"}
                    {!auth?.creating ? (
                      <HiOutlineArrowRight size={18} aria-hidden />
                    ) : null}
                  </button>
                </form>

                <div className="pi-login__or" aria-hidden="true">
                  <span>or</span>
                </div>

                <GoogleAuthButton
                  text="signup_with"
                  disabled={busy}
                  onCredential={onGoogleCredential}
                />
              </>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}

const mapStateToProps = (state) => ({
  auth: state.auth,
});

const mapDispatchToProps = (dispatch) => ({
  login: (data, callBack) =>
    dispatch({
      type: LOGIN,
      payload: {
        type: LOGIN,
        method: POST,
        label: Labels.login,
        data,
        callBack,
      },
    }),
  googleLogin: (data, callBack) =>
    dispatch({
      type: LOGIN,
      payload: {
        type: LOGIN,
        method: POST,
        label: Labels.googleAuth,
        hideOverlayLoader: true,
        data,
        callBack,
      },
    }),
  signUp: (data) =>
    dispatch({
      type: SIGN_UP,
      payload: { type: SIGN_UP, method: POST, label: Labels.signUp, data },
    }),
  toasterShow: (data) =>
    dispatch({
      type: TOASTER_SHOW,
      payload: { ...data },
    }),
});

export default connect(mapStateToProps, mapDispatchToProps)(ValuationAuthModal);
